/// <amd-module name="VSSUI/Components/Tabs/TabList" />
import * as React from "react";

import { ObservableLike } from "VSS/Core/Observable";
import {
    ITabGroup,
    ITabGroupProviderContext,
    ITabListProps,
    ITabProps,
    ITabProviderContext,
    IVssContributedTab,
    Orientation
} from "VSSUI/Components/Tabs/Tabs.Props";
import { css } from "VSSUI/Util";
import { Tab } from "./Tab";
import { TabGroupProviderContext } from "./TabGroupProviderContext";
import { TabProviderContext } from "./TabProviderContext";
import { Tabs } from "./Tabs";

export class TabList extends React.Component<ITabListProps> {
    public constructor(props: ITabListProps) {
        super(props);
    }

    public render() {
        return (
            <TabGroupProviderContext.Consumer>
                {(provider: ITabGroupProviderContext) => {
                    const { className, tabGroups } = this.props;
                    const mergedGroups: ITabGroup[] = tabGroups ? tabGroups.concat(provider.groups) : provider.groups;
                    const groupMap = this.generateGroupMap(mergedGroups);

                    return (
                        <TabProviderContext.Consumer>
                            {(provider: ITabProviderContext) => {
                                const selectedTabId = this.props.selectedTabId || provider.selectedId;
                                const tabGroups = this.processContributions(provider.tabs, groupMap, this.processChildren(groupMap));
                                const trueChildren = this.processGroups(tabGroups);

                                return (
                                    <div className={css(className, "bolt-tablist flex-column")}>
                                        {this.props.listTitle && this.renderTitle(this.props.listTitle)}
                                        {this.props.listSubTitle && this.renderSubTitle(this.props.listSubTitle)}
                                        <Tabs
                                            ariaLabel={this.props.listTitle}
                                            selectedTabId={selectedTabId}
                                            {...this.props}
                                            className="bolt-tablist-tabs"
                                            orientation={Orientation.Vertical}
                                        >
                                            {trueChildren}
                                        </Tabs>
                                    </div>
                                );
                            }}
                        </TabProviderContext.Consumer>
                    );
                }}
            </TabGroupProviderContext.Consumer>
        );
    }

    private generateGroupMap = (groups: ITabGroup[]): IRawGroupMap => {
        if (!groups) {
            return {};
        }

        const map: IRawGroupMap = {};
        groups.forEach((group: ITabGroup) => {
            map[group.id] = group;
        });
        return map;
    };

    private processChildren = (groups: IRawGroupMap): ITabGroupResultMap => {
        const childGroups: ITabGroupResultMap = {};

        React.Children.forEach(this.props.children, (child: React.ReactElement<ITabProps>) => {
            if (child) {
                const groupId = child.props.groupId || "";

                if (!childGroups[groupId]) {
                    childGroups[groupId] = {
                        items: [child],
                        groupProps: groups[groupId] || {
                            id: "",
                            name: "",
                            order: -1
                        }
                    };
                } else {
                    childGroups[groupId].items.push(child);
                }
            }
        });

        return childGroups;
    };

    private processContributions = (tabs: IVssContributedTab[], baseGroups: IRawGroupMap, childGroups: ITabGroupResultMap): ITabGroupResultMap => {
        tabs.forEach((tab: IVssContributedTab) => {
            const mappedTab = this.createRow(tab);
            const groupId = tab.groupId || "";

            if (!childGroups[groupId]) {
                childGroups[groupId] = {
                    items: [mappedTab],
                    groupProps: baseGroups[groupId] || {
                        id: "",
                        name: "",
                        order: -1
                    }
                };
            } else {
                childGroups[groupId].items.push(mappedTab);
            }
        });

        return childGroups;
    };

    private processGroups = (groups: ITabGroupResultMap): JSX.Element[] => {
        const elements: JSX.Element[] = [];

        // Flattening to allow for sort
        const flatMappedGroups: ITabGroupResult[] = [];
        for (const groupId in groups) {
            flatMappedGroups.push(groups[groupId]);
        }

        flatMappedGroups
            .sort((a, b) => (a.groupProps.order || 1000) - (b.groupProps.order || 1000))
            .forEach((group: ITabGroupResult) => {
                group.groupProps.name &&
                    elements.push(
                        <div key={group.groupProps.name} className="bolt-tablist-heading title-xs flex-noshrink">
                            {group.groupProps.name}
                        </div>
                    );
                elements.push(...group.items);
            });

        return elements;
    };

    private createRow = (tab: IVssContributedTab): JSX.Element => {
        return (
            <Tab
                key={tab.id}
                id={tab.id}
                name={tab.name}
                url={tab.url && ObservableLike.getValue(tab.url)}
                onBeforeTabChange={tab.onBeforeTabChange}
                iconProps={tab.iconProps}
            />
        );
    };

    private renderTitle = (header: string): JSX.Element => {
        return <div className="bolt-tablist-title title-m flex-noshrink">{header}</div>;
    };

    private renderSubTitle = (header: string): JSX.Element => {
        return <div className="bolt-tablist-subtitle secondary-text flex-noshrink text-ellipsis">{header}</div>;
    };

    private onSelectedTabIdChanged = () => {
        this.forceUpdate();
    };
}

interface IRawGroupMap {
    [groupId: string]: ITabGroup;
}

interface ITabGroupResultMap {
    [groupId: string]: ITabGroupResult;
}

interface ITabGroupResult {
    items: React.ReactElement<ITabProps>[];
    groupProps: ITabGroup;
}
